<template>
  <div class="agencies-container">
    <TableFiltersContainer padding="2px 0px 12px 0px">
      <BusinessUnitSelect v-model:business-unit-ids="commercialConditionState.selectedFilters.businessUnitIds" />
      <TableFiltersResetButton @click="resetFilters" />
      <AwButton color="secondary" variant="outline" class="ml-auto" @click="showCreateModal = true">
        <AwIcon name="i-ph-plus" />
        Créer de nouvelles conditions
      </AwButton>
    </TableFiltersContainer>
    <TableInteractive
      :key="tableKey"
      v-model:sorting="commercialConditionState.sorting"
      :loading="commercialConditionState.loading"
      :enable-sorting="true"
      :manual-sorting="true"
      :data="commercialConditions"
      :columns="tableColumns"
      :empty-state-type="emptyStateType"
      :empty-state-reset-filters="resetFilters"
      height="100%"
      :on-select="(row: any) => (openedCommercialCondition = row.original)"
    >
      <template #status-cell="{ row }">
        <TableCellContainer :is-loading="commercialConditionState.loading" :skeletons="['text']">
          <AwBadge :color="mapButtonStatusToAwVariant(row.original.status)">
            {{ translateCommercialConditionStatus.fr[row.original.status as CommercialConditionStatus] }}
          </AwBadge>
        </TableCellContainer>
      </template>
      <template #type-cell="{ row }">
        <TableCellContainer :is-loading="commercialConditionState.loading" :skeletons="['text']">
          <div class="flex flex-row items-center gap-1">
            <AwIcon name="i-ph-files" />
            <TextEllipsis>
              {{ translateCommercialConditionType.fr[row.original.type as CommercialConditionType] }}
            </TextEllipsis>
          </div>
        </TableCellContainer>
      </template>
      <template #created_at-cell="{ row }">
        <TableCellContainer :is-loading="commercialConditionState.loading" :skeletons="['text']">
          <TableCellDate :date="row.original.created_at" format="distanceToNow" />
        </TableCellContainer>
      </template>
      <template #created_by-cell="{ row }">
        <TableCellContainer :is-loading="commercialConditionState.loading" :skeletons="['text']">
          <TableCellTeamMember :team-member="row.original.auth_user.team_member" />
        </TableCellContainer>
      </template>
      <template #business_unit-cell="{ row }">
        <TableCellContainer :is-loading="commercialConditionState.loading" :skeletons="['text']">
          <TextEllipsis> {{ row.original.business_unit?.emoji }} {{ row.original.business_unit?.name }} </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #footer>
        <TablePagination
          :total-items="commercialConditionState.totalItems"
          :page-size="commercialConditionState.selectedFilters.pageSize"
          :page-index="commercialConditionState.selectedFilters.pageIndex"
          @update:page-size="commercialConditionState.selectedFilters.pageSize = $event"
          @update:page-index="commercialConditionState.selectedFilters.pageIndex = $event"
        />
      </template>
    </TableInteractive>
  </div>
  <LazyCommercialConditionUpsertDialog
    v-if="showCreateModal"
    :creation-company-id="companyId"
    @on-close="showCreateModal = false"
    @on-create="query()"
  />
  <LazyCommercialConditionSheet
    v-if="openedCommercialCondition"
    v-model:commercial-condition="openedCommercialCondition"
    :is-readable="isReadable"
    @on-close="openedCommercialCondition = undefined"
    @update:commercial-condition="query()"
  />
</template>

<script setup lang="ts">
import { useUserMe } from "~/components/user/composables/useUser";
import { useToast } from "~/composables/useToast";
import type { TableEmptyStateType } from "~/utils/types";
import { useTableColumn } from "~/composables/useTableColumn";
import { useCommercialCondition } from "~/components/commercial-condition/composables/useCommercialCondition";
import type { CommercialCondition } from "@asap/shared/utils/supabase.types";
import type { CommercialConditionRequest } from "@asap/shared/schemas/commercial-condition.schema";
import type { CommercialConditionType, CommercialConditionStatus } from "@asap/shared/utils/supabase.enum";
import { translateCommercialConditionType, translateCommercialConditionStatus } from "@asap/shared/utils/translate";

const props = defineProps<{
  companyId: string;
}>();

const showCreateModal = ref(false);
const emptyStateType = ref<TableEmptyStateType>("no-data");
const commercialConditions = ref<CommercialConditionRequest[]>([]);
const openedCommercialCondition = ref<CommercialCondition | undefined>(undefined);

const { workspaceId } = useUserMe();
const { addToastError } = useToast();
const { commercialConditionState, selectMultipleForTable, convertSortingToOrders, mapButtonStatusToAwVariant } =
  useCommercialCondition();

const isReadable = computed<boolean>(() => {
  if (!openedCommercialCondition.value) return false;

  const commercialConditionOwnerWorkspaceId = openedCommercialCondition.value.auth_user.team_member?.workspace_id;
  const isOwnCondition = workspaceId.value === commercialConditionOwnerWorkspaceId;
  const isToValidate = openedCommercialCondition.value.status === "to_validate";

  // Block independents from viewing their own to_validate conditions (prevents workflow bypass)
  if (openedCommercialCondition.value && isOwnCondition && isToValidate) {
    return false;
  }

  return true;
});

const { tableKey, tableColumns } = useTableColumn(
  [
    {
      label: "Statut",
      value: {
        accessorKey: "status",
        header: "Statut",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Type",
      value: {
        accessorKey: "type",
        header: "Type",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Création",
      value: {
        accessorKey: "created_at",
        header: "Création",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Créateur",
      value: {
        accessorKey: "created_by",
        header: "Créateur",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Business Unit",
      value: {
        accessorKey: "business_unit",
        header: "Business Unit",
      },
      blocked: true,
      selected: true,
    },
  ],
  "company-insurances-table-columns"
);

const resetFilters = () => {
  commercialConditionState.selectedFilters.search = undefined;
  commercialConditionState.selectedFilters.status = undefined;
  commercialConditionState.selectedFilters.businessUnitIds = undefined;
  commercialConditionState.sorting = [{ id: "created_at", desc: true }];
};

const query = async function () {
  try {
    const orders = convertSortingToOrders(commercialConditionState.sorting);
    const { data, count } = await selectMultipleForTable({
      ...commercialConditionState.selectedFilters,
      companyId: props.companyId,
      orders,
    });
    if (data) commercialConditions.value = data;
    if (count !== null) commercialConditionState.totalItems = count;
    if (commercialConditionState.selectedFilters.search) {
      emptyStateType.value = "no-data-with-filters";
    } else {
      emptyStateType.value = "no-data";
    }
  } catch (error) {
    emptyStateType.value = "error";
    addToastError(
      {
        title: "Une erreur est survenue lors de la récupération des conditions commerciales",
      },
      error
    );
  }
};

const isInitialized = ref(false);

onMounted(async () => {
  commercialConditionState.selectedFilters.pageIndex = 0;
  await query();
  isInitialized.value = true;
});

watch(() => commercialConditionState.selectedFilters.pageIndex, query);

watch(
  () => [
    commercialConditionState.sorting,
    commercialConditionState.selectedFilters.search,
    commercialConditionState.selectedFilters.pageSize,
    commercialConditionState.selectedFilters.status,
    commercialConditionState.selectedFilters.companyId,
    commercialConditionState.selectedFilters.orders,
    commercialConditionState.selectedFilters.businessUnitIds,
  ],
  async () => {
    if (!isInitialized.value) return;
    commercialConditionState.selectedFilters.pageIndex = 0;
    await query();
  }
);
</script>

<style scoped lang="scss">
.agencies-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.toggle_wrapper {
  margin: 0 auto;
  margin-top: 1rem;
}
</style>
